import type {
  GeoJsonDocument,
  GeoJsonFeature,
  GeoJsonFeatureCollection,
  GeoJsonGeometry,
  JsonValue,
  SimpleDrawingFeature
} from "../../core/types/geojson";

export class GeoJsonParser {
  static parse(text: string): GeoJsonDocument {
    const parsed = JSON.parse(text) as unknown;
    this.assertGeoJsonDocument(parsed);
    return parsed;
  }

  static stringify(document: unknown): string {
    return JSON.stringify(document, null, 2);
  }

  static normalizeToFeatureCollection(document: GeoJsonDocument): GeoJsonFeatureCollection {
    if (document.type === "FeatureCollection") return document;
    if (document.type === "Feature") return { type: "FeatureCollection", features: [document] };
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: {}, geometry: document }]
    };
  }

  static flattenForDrawing(document: GeoJsonDocument): SimpleDrawingFeature[] {
    const collection = this.normalizeToFeatureCollection(document);
    return collection.features.flatMap((feature) => this.flattenFeature(feature));
  }

  static sanitizeProperties(properties: Record<string, unknown> | null | undefined): Record<string, JsonValue> {
    const safe: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(properties ?? {})) {
      if (this.isJsonValue(value)) safe[key] = value;
      else safe[key] = String(value);
    }
    return safe;
  }

  private static flattenFeature(feature: GeoJsonFeature): SimpleDrawingFeature[] {
    if (!feature.geometry) return [];
    const properties = this.sanitizeProperties(feature.properties);
    const id = feature.id;
    const makeFeature = (geometry: SimpleDrawingFeature["geometry"], suffix: string | number): SimpleDrawingFeature => ({
      type: "Feature",
      id: id === undefined ? undefined : `${id}-${suffix}`,
      properties,
      geometry
    });

    const geometry = feature.geometry;
    switch (geometry.type) {
      case "Point":
      case "LineString":
      case "Polygon":
        return [{ type: "Feature", id, properties, geometry }];
      case "MultiPoint":
        return geometry.coordinates.map((coordinates, index) => makeFeature({ type: "Point", coordinates }, index));
      case "MultiLineString":
        return geometry.coordinates.map((coordinates, index) => makeFeature({ type: "LineString", coordinates }, index));
      case "MultiPolygon":
        return geometry.coordinates.map((coordinates, index) => makeFeature({ type: "Polygon", coordinates }, index));
      case "GeometryCollection":
        return geometry.geometries.flatMap((item, index) =>
          this.flattenFeature({ type: "Feature", id: id === undefined ? undefined : `${id}-${index}`, properties, geometry: item })
        );
      default:
        return [];
    }
  }

  private static assertGeoJsonDocument(value: unknown): asserts value is GeoJsonDocument {
    if (!value || typeof value !== "object") throw new Error("GeoJSON must be a JSON object.");
    const type = (value as { type?: unknown }).type;
    if (typeof type !== "string") throw new Error("GeoJSON object must include a type property.");
    const allowed = new Set(["FeatureCollection", "Feature", "Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon", "GeometryCollection"]);
    if (!allowed.has(type)) throw new Error(`Unsupported GeoJSON type: ${type}`);
  }

  private static isJsonValue(value: unknown): value is JsonValue {
    if (value === null) return true;
    if (["string", "number", "boolean"].includes(typeof value)) return true;
    if (Array.isArray(value)) return value.every((item) => this.isJsonValue(item));
    if (typeof value === "object") return Object.values(value as Record<string, unknown>).every((item) => this.isJsonValue(item));
    return false;
  }
}
